import { DependencyList, useEffect, useMemo, useRef } from 'react';
import {
    AsyncValueState,
    asyncValueCreatePendingState,
    asyncValueCreateRejectedState,
    asyncValueCreateResolvedState
} from '@wix/devzai-utils-common';
import { useForceUpdate } from './use-force-update';

export function useAsyncValue<T>(asyncValueFn: () => Promise<T>, deps: DependencyList): AsyncValueState<T> {
    const asyncStateInfoRef = useRef<AsyncValueState<T>>();
    const forceUpdate = useForceUpdate();

    const pendingPromiseRef = useRef<Promise<T>>();

    useMemo(() => {
        const promise = (pendingPromiseRef.current = asyncValueFn());

        asyncStateInfoRef.current = asyncValueCreatePendingState(promise);

        promise
            .then(value => {
                if (pendingPromiseRef.current === promise) {
                    asyncStateInfoRef.current = asyncValueCreateResolvedState(value);
                    forceUpdate();
                }
            })
            .catch(error => {
                if (pendingPromiseRef.current === promise) {
                    asyncStateInfoRef.current = asyncValueCreateRejectedState(error);
                    forceUpdate();
                }
            });
    }, deps);

    useEffect(() => {
        return () => {
            pendingPromiseRef.current = undefined;
        };
    }, []);

    return asyncStateInfoRef.current as any;
}
